<!-- SelectPeriodFilter.vue -->
<!--
  Mini-modal de filtro por período.
  Oferece seleção de campo (CreatedAt / UpdatedAt) + duas datas (de / até).

  Props:
    isOpen     — controla visibilidade
    modelValue — { field: 'createdAt'|'updatedAt', dateFrom: string, dateTo: string }

  Emits:
    update:modelValue — novo valor
    apply             — usuário clicou em "Filtrar"
    close
-->
<script setup>
import { ref, watch } from "vue";
import AnchoredPanel from "@/components/Generics/AnchoredPanel.vue";

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  modelValue: {
    type: Object,
    default: () => ({ field: "updatedAt", dateFrom: "", dateTo: "" }),
  },
});

const emit = defineEmits(["update:modelValue", "apply", "close"]);

// Cópia local
const localValue = ref({ ...props.modelValue });

watch(
  () => props.modelValue,
  (val) => { localValue.value = { ...val }; },
  { deep: true }
);

watch(
  () => props.isOpen,
  (newVal) => {
    if (newVal) localValue.value = { ...props.modelValue };
  }
);

function applyFilter() {
  emit("update:modelValue", { ...localValue.value });
  emit("apply");
}

function clearFilter() {
  localValue.value = { field: "updatedAt", dateFrom: "", dateTo: "" };
  emit("update:modelValue", { ...localValue.value });
}
</script>

<template>
  <AnchoredPanel
    :open="isOpen"
    placement="side"
    align="left"
    :gap="8"
    @close="emit('close')"
  >
    <div
      class="period-filter-container"
      role="dialog"
      aria-label="Filtrar por período"
      @click.stop
      @mousedown.stop
    >
      <div class="body-section">
        <!-- Seleção de campo -->
        <div class="field-row">
          <label class="field-label">Campo</label>
          <div class="radio-group">
            <label class="radio-item">
              <input
                v-model="localValue.field"
                type="radio"
                value="createdAt"
                class="radio-input"
              />
              <span>Criação</span>
            </label>
            <label class="radio-item">
              <input
                v-model="localValue.field"
                type="radio"
                value="updatedAt"
                class="radio-input"
              />
              <span>Atualização</span>
            </label>
          </div>
        </div>

        <!-- Data de -->
        <div class="date-block">
          <label class="field-label" for="period-date-from">De</label>
          <input
            id="period-date-from"
            v-model="localValue.dateFrom"
            type="date"
            class="date-input"
          />
        </div>

        <!-- Data até -->
        <div class="date-block">
          <label class="field-label" for="period-date-to">Até</label>
          <input
            id="period-date-to"
            v-model="localValue.dateTo"
            type="date"
            class="date-input"
          />
        </div>
      </div>

      <div class="modal-actions">
        <button type="button" class="btn btn-secondary btn-sm" @click="clearFilter">
          Limpar
        </button>
        <button type="button" class="btn btn-primary btn-sm" @click="applyFilter">
          Filtrar
        </button>
      </div>
    </div>
  </AnchoredPanel>
</template>

<style scoped>
.period-filter-container {
  background-color: var(--surface);
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
  border: 1px solid var(--border-color);
  width: 230px;
  display: flex;
  flex-direction: column;
}

.body-section {
  padding: 10px 10px 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field-label {
  font-size: 0.78rem;
  color: #666;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  display: block;
  margin-bottom: 4px;
}

/* Linha de seleção de campo */
.field-row {
  display: flex;
  flex-direction: column;
}

.radio-group {
  display: flex;
  gap: 12px;
}

.radio-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.82rem;
  color: var(--text-dark);
  cursor: pointer;
  font-weight: 400;
}

.radio-input {
  accent-color: var(--brand-light);
  cursor: pointer;
}

/* Linha de data */
/* data-block: label acima, input abaixo — ocupam linha inteira */
.date-block {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.date-input {
  background-color: #f7f7f7;
  border: 1px solid var(--border-color);
  color: var(--text-dark);
  font-size: 0.82rem;
  border-radius: 8px;
  padding: 5px 8px;
  outline: none;
  width: 100%;
  box-sizing: border-box;
}

.date-input:focus {
  border-color: var(--brand-light);
}

/* Ações */
.modal-actions {
  padding: 8px 10px;
  border-top: 1px solid var(--border-color);
  margin: 2px 10px 0 10px;
  display: flex;
  justify-content: flex-end;
  gap: 0.4rem;
}

.btn-sm {
  height: 26px;
  padding: 0 0.7rem;
  font-size: 0.8rem;
  font-weight: 600;
}

</style>